import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Youtube, CheckCircle2, Link2, ShieldCheck, Bot, Play, Activity, Zap } from "lucide-react";

const ytImg = "https://images.pexels.com/photos/33440446/pexels-photo-33440446.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [ap, setAp] = useState(null);
  const [running, setRunning] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/settings").then((r) => setStatus(r.data));
  const loadAp = () => api.get("/autopilot").then((r) => setAp(r.data));
  useEffect(() => {
    load(); loadAp();
    const iv = setInterval(loadAp, 15000);
    if (params.get("youtube") === "connected") { toast.success("YouTube channel connected!"); setParams({}); }
    if (params.get("youtube") === "error") { toast.error("YouTube connection failed"); setParams({}); }
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const toggleAp = async (field, value) => {
    setAp((p) => ({ ...p, [field]: value }));
    await api.post("/autopilot", { [field]: value });
    toast.success(field === "ap_enabled" ? (value ? "Autopilot ON" : "Autopilot paused") : "Updated");
    loadAp();
  };

  const setCap = async (cap) => {
    setAp((p) => ({ ...p, ap_daily_cap: cap }));
    await api.post("/autopilot", { ap_daily_cap: cap });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/autopilot/run-now");
      toast.success("Autopilot run started — new videos will appear in your Library shortly");
    } catch (e) { toast.error("Failed to start"); }
    setTimeout(() => { setRunning(false); loadAp(); }, 3000);
  };

  const saveKey = async () => {
    if (!apiKey.trim()) { toast.error("Enter a YouTube API key"); return; }
    await api.post("/settings", { youtube_api_key: apiKey });
    toast.success("YouTube API key saved"); setApiKey(""); load();
  };

  const saveOauth = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { toast.error("Enter both client ID and secret"); return; }
    await api.post("/settings", { oauth_client_id: clientId, oauth_client_secret: clientSecret });
    toast.success("OAuth credentials saved"); setClientId(""); setClientSecret(""); load();
  };

  const connect = async () => {
    try {
      const r = await api.get("/youtube/auth-url");
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Configure OAuth credentials first");
    }
  };

  const StatusRow = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-sm">
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-signal-green" : "bg-white/20"}`} />
      <span className={ok ? "text-white" : "text-white/40"}>{label}</span>
      {ok && <CheckCircle2 className="w-3.5 h-3.5 text-signal-green" />}
    </div>
  );

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <PageHeader eyebrow="Connections" title="Settings" testid="settings-header"
        subtitle="Connect real YouTube data and enable one-click publishing to your channel." />

      <div className="bg-gradient-to-br from-[#161616] to-[#121212] border border-white/10 rounded-md p-6 mb-6 fade-up relative overflow-hidden" data-testid="autopilot-card">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
        <div className="flex items-start justify-between gap-4 relative">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-md flex items-center justify-center border ${ap?.ap_enabled ? "bg-brand/15 border-brand/40" : "bg-white/5 border-white/10"}`}>
              <Bot className={`w-5 h-5 ${ap?.ap_enabled ? "text-brand pulse-dot" : "text-white/40"}`} />
            </div>
            <div>
              <h3 className="font-heading text-xl flex items-center gap-2">Autopilot
                {ap?.ap_enabled && <span className="text-[9px] uppercase tracking-[0.2em] font-mono text-signal-green border border-signal-green/30 rounded px-1.5 py-0.5">Live</span>}
              </h3>
              <p className="text-white/40 text-sm mt-0.5">Auto-discovers hot niches & produces ready videos every 6 hours.</p>
            </div>
          </div>
          <Switch data-testid="autopilot-toggle" checked={!!ap?.ap_enabled} onCheckedChange={(v) => toggleAp("ap_enabled", v)} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 relative">
          <div className="bg-[#0A0A0A] border border-white/10 rounded p-3">
            <div className="text-[9px] uppercase tracking-wider text-white/30 font-mono">Produced Today</div>
            <div className="font-mono text-2xl mt-1">{ap?.produced_today ?? 0}<span className="text-sm text-white/30">/{ap?.ap_daily_cap ?? 3}</span></div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded p-3">
            <div className="text-[9px] uppercase tracking-wider text-white/30 font-mono">Total Auto-Made</div>
            <div className="font-mono text-2xl mt-1 text-signal-green">{ap?.total_autopilot ?? 0}</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded p-3">
            <div className="text-[9px] uppercase tracking-wider text-white/30 font-mono">Frequency</div>
            <div className="font-mono text-lg mt-1.5">Every 6h</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded p-3">
            <div className="text-[9px] uppercase tracking-wider text-white/30 font-mono mb-1.5">Daily Cap</div>
            <div className="flex gap-1">
              {[1, 3, 5].map((n) => (
                <button key={n} data-testid={`cap-${n}`} onClick={() => setCap(n)}
                  className={`flex-1 text-xs font-mono rounded py-1 border transition-colors ${ap?.ap_daily_cap === n ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/40 hover:text-white"}`}>{n}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5 relative">
          <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
            <Switch data-testid="autopublish-toggle" checked={!!ap?.ap_auto_publish} onCheckedChange={(v) => toggleAp("ap_auto_publish", v)} />
            Auto-publish scheduled posts when due
            {!status?.youtube_connected && <span className="text-[10px] text-signal-yellow font-mono">(connect YouTube below)</span>}
          </label>
          <button data-testid="autopilot-run-now" onClick={runNow} disabled={running}
            className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white rounded-md px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 justify-center">
            <Play className="w-4 h-4" /> {running ? "Starting..." : "Run Now"}
          </button>
        </div>

        {(ap?.recent_runs || []).length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/10 relative">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono mb-2 flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> Activity Log</div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {ap.recent_runs.map((r, i) => (
                <div key={`${r.at}-${i}`} className="flex items-start gap-2 text-xs" data-testid={`ap-log-${i}`}>
                  <Zap className={`w-3 h-3 mt-0.5 shrink-0 ${r.count > 0 ? "text-signal-green" : "text-white/30"}`} />
                  <span className="text-white/60 flex-1">{r.message}</span>
                  <span className="font-mono text-white/30 shrink-0">{new Date(r.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-[10px] text-white/30 mt-4 font-mono relative">Note: Autopilot creates packages, thumbnails, variants & schedules automatically. Pushing the actual video file to YouTube still needs your uploaded video + connected channel.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 fade-up">
        <div className="lg:col-span-1 bg-surface border border-white/10 rounded-md p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-4">Connection Status</div>
          <div className="space-y-3">
            <StatusRow ok={status?.youtube_api_key_set} label="Trending Data API" />
            <StatusRow ok={status?.oauth_configured} label="OAuth Configured" />
            <StatusRow ok={status?.youtube_connected} label="Channel Connected" />
          </div>
          {status?.channel_title && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-wider text-white/30 font-mono">Channel</div>
              <div className="text-sm text-signal-green mt-1 font-medium">{status.channel_title}</div>
            </div>
          )}
        </div>
        <div className="lg:col-span-2 rounded-md overflow-hidden border border-white/10 relative">
          <img src={ytImg} alt="youtube" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent flex items-end p-6">
            <div>
              <div className="font-heading text-2xl">Publish without leaving the app</div>
              <p className="text-white/60 text-sm mt-1">Generate, assemble, and ship straight to YouTube.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-surface border border-white/10 rounded-md p-6 mb-4 fade-up">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-signal-yellow" /><h3 className="font-heading text-lg">YouTube Data API Key</h3></div>
        <p className="text-white/40 text-sm mb-4">Powers real trending video data inside the Trend Radar. Create one at <a className="text-signal-blue hover:underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> (enable "YouTube Data API v3").</p>
        <div className="flex gap-2">
          <input data-testid="api-key-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="AIza..."
            className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 outline-none focus:border-brand text-sm" />
          <button data-testid="save-api-key-btn" onClick={saveKey} className="bg-white/10 hover:bg-white/15 rounded-md px-5 py-2.5 text-sm font-medium transition-colors">Save</button>
        </div>
      </div>

      <div className="bg-surface border border-white/10 rounded-md p-6 fade-up">
        <div className="flex items-center gap-2 mb-1"><Youtube className="w-4 h-4 text-brand" /><h3 className="font-heading text-lg">Channel Upload (OAuth)</h3></div>
        <p className="text-white/40 text-sm mb-4">Enables uploading finished videos to your channel. Create an OAuth 2.0 Client (type: Web) in Google Cloud and add this redirect URI: <code className="text-white/70 bg-black/40 px-1.5 py-0.5 rounded text-xs break-all">{process.env.REACT_APP_BACKEND_URL}/api/youtube/callback</code></p>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">OAuth Client ID</label>
            <input data-testid="client-id-input" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxx.apps.googleusercontent.com"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 outline-none focus:border-brand text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">OAuth Client Secret</label>
            <input data-testid="client-secret-input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-..."
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 outline-none focus:border-brand text-sm" />
          </div>
          <div className="flex gap-2 pt-1">
            <button data-testid="save-oauth-btn" onClick={saveOauth} className="bg-white/10 hover:bg-white/15 rounded-md px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Save Credentials
            </button>
            <button data-testid="connect-youtube-btn" onClick={connect} disabled={!status?.oauth_configured}
              className="bg-brand hover:bg-brand-hover disabled:opacity-40 text-white rounded-md px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2">
              <Link2 className="w-4 h-4" /> {status?.youtube_connected ? "Reconnect Channel" : "Connect Channel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
